import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { IndexedDBService } from './indexeddb.service';
import { PreviewV2Service } from './v2/preview-v2.service';
import { 
  TagBasedGenerationRequest, 
  TagBasedGenerationResponse, 
  BusinessData,
  ContentPreferences,
  TagReplacement,
  ContentUpdate,
  DictContentStructure
} from '../interfaces/ai-content.interface';
import { SupplierData } from '../interfaces/supplier-data.interface';

@Injectable({
  providedIn: 'root'
})
export class AIContentService {

  constructor(
    private http: HttpClient,
    private indexedDBService: IndexedDBService,
    private previewService: PreviewV2Service
  ) {}

  /**
   * Generate AI content using dict.js format and automatically apply it
   * This is the new method that handles the dict.js structure
   */
  async generateAndApplyDictContent(
    supplierData: SupplierData, 
    templateHtml: string
  ): Promise<{ updatedHtml: string; contentUpdates: ContentUpdate[]; hasChanges: boolean }> {
    try {
      const businessData = this.convertSupplierDataToBusinessData(supplierData);
      
      const response = await this.generateTagBasedContent({
        business_data: [businessData],
        content_preferences: this.getDefaultContentPreferences()
      });

      if (response.status === 'success' && response.data) {
        return await this.handleDictContentResponse(response.data as DictContentStructure, templateHtml);
      } else {
        return {
          updatedHtml: templateHtml,
          contentUpdates: [],
          hasChanges: false
        };
      }
    } catch (error) {
      console.error('Dict content generation and application failed:', error);
      throw error;
    }
  }

  /**
   * Handle dict content response - convert to tag replacements and store
   */
  private async handleDictContentResponse(
    dictContent: DictContentStructure, 
    templateHtml: string
  ): Promise<{ updatedHtml: string; contentUpdates: ContentUpdate[]; hasChanges: boolean }> {
    try {
      // Convert dict structure to tag replacements for backward compatibility
      const tagReplacements = this.convertDictToTagReplacements(dictContent);
      const supplierData = await this.indexedDBService.getUserSupplierData(this.getStoredCompanyGuid() || '');

      
      console.log('AIContentService: Supplier data:', supplierData);

      dictContent.CompanyInfo.COMPANY_NAME = supplierData.companyName;
      dictContent.CompanyInfo.COMPANY_EMAIL = supplierData.contactEmail;
      dictContent.CompanyInfo.COMPANY_PHONE = supplierData.phoneNumber;
      dictContent.CompanyInfo.COMPANY_ADDRESS = dictContent.CompanyInfo.COMPANY_ADDRESS = [
        supplierData.streetAddress,
        supplierData.city,
        supplierData.countryRegion,
        supplierData.postalCode
      ].filter(Boolean).join(', ');
      dictContent.CompanyInfo.COMPANY_COPYRIGHT = `© 2026 ${supplierData.companyName}. All Rights Reserved.`;
      
      // Apply the replacements
      const result = this.applyTagReplacements(templateHtml, tagReplacements);

       // Get existing data
      const existingData = await this.indexedDBService.getDictContent(this.getStoredCompanyGuid() || '');

      // Merge with new data (preserving existing AboutSection if new data doesn't have it)
      const mergedData = {
        ...existingData,
        ...dictContent  // This will override existing sections but preserve missing ones
      };
      
      // Store dict content in IndexedDB
      await this.indexedDBService.storeDictContent(this.getStoredCompanyGuid() || '', mergedData);
      
      // Update the dict.js file through the preview service
      console.log('Updating dict.js file with new content...');
      await this.previewService.updateDictJsFile(mergedData);
      
      return result;
    } catch (error) {
      console.error('Failed to handle dict content response:', error);
      throw error;
    }
  }

  /**
   * Check if response data is DictContentStructure
   */
  private isDictContentStructure(data: any): data is DictContentStructure {
    return data && typeof data === 'object' && 'CompanyInfo' in data && 'HeroSection' in data;
  }

  /**
   * Convert dict structure to TagReplacement[] for backward compatibility
   */
  private convertDictToTagReplacements(dictContent: DictContentStructure): TagReplacement[] {
    const tagReplacements: TagReplacement[] = [];
    
    // Flatten the nested structure
    this.flattenDictContent(dictContent, '', tagReplacements);
    
    return tagReplacements;
  }

  /**
   * Recursively flatten dict content
   */
  private flattenDictContent(obj: any, prefix: string, result: TagReplacement[]): void {
    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        this.flattenDictContent(value, fullKey, result);
      } else if (typeof value === 'string') {
        result.push({
          tagName: fullKey,
          replacementHTML: value
        });
      }
    }
  }


  /**
   * Apply tag replacements to HTML content
   */
  applyTagReplacements(
    htmlContent: string,
    tagReplacements: TagReplacement[]
  ): { updatedHtml: string; contentUpdates: ContentUpdate[]; hasChanges: boolean } {
    let updatedHtml = htmlContent;
    const contentUpdates: ContentUpdate[] = [];
    let hasChanges = false;

    for (const replacement of tagReplacements) {
      const { tagName, replacementHTML } = replacement;
      
      // Create regex pattern to match the tag
      const tagPattern = new RegExp(`{{\\s*${tagName}\\s*}}`, 'g');
      
      if (tagPattern.test(updatedHtml)) {
        const originalContent = updatedHtml.match(tagPattern)?.[0] || '';
        updatedHtml = updatedHtml.replace(tagPattern, replacementHTML);
        
        contentUpdates.push({
          tagName,
          context: this.inferTagContext(htmlContent, tagName),
          originalContent,
          newContent: replacementHTML,
          isChanged: true
        });
        
        hasChanges = true;
      }
    }

    return { updatedHtml, contentUpdates, hasChanges };
  }

  /**
   * Generate tag-based content from AI service
   */
  async generateTagBasedContent(request: TagBasedGenerationRequest): Promise<TagBasedGenerationResponse> {
    try {
      const response = await firstValueFrom(
        this.http.post<TagBasedGenerationResponse>(
          `${environment.fastApiBaseUrl}/ai/generate-hierarchical-content`,
          request
        )
      );
      return response;
    } catch (error) {
      console.error('AI content generation failed:', error);
      throw error;
    }
  }

  /**
   * Regenerate content for a specific semantic tag based on user feedback
   */
  async regenerateSingleTag(
    tagName: string, 
    currentContent: string, 
    userFeedback: string, 
    supplierData: SupplierData
  ): Promise<TagReplacement[]> {
    try {
      // Convert supplier data to business data format
      const businessData = this.convertSupplierDataToBusinessData(supplierData);
      
      const request = {
        tag_name: tagName,
        current_content: currentContent,
        user_feedback: userFeedback,
        business_data: businessData
      };

      const response = await firstValueFrom(
        this.http.post<TagBasedGenerationResponse>(
          `${environment.fastApiBaseUrl}/ai/regenerate-single-tag`,
          request
        )
      );

      if (response.status === 'success' && response.data) {
        if (Array.isArray(response.data)) {
          return response.data;
        } else {
          // Convert dict structure to TagReplacement[]
          return this.convertDictToTagReplacements(response.data as DictContentStructure);
        }
      } else {
        console.warn('Single tag regeneration returned no data:', response);
        return [];
      }
    } catch (error) {
      console.error('Single tag regeneration failed:', error);
      throw error;
    }
  }

  /**
   * Fetch supplier metadata (contact_metadata and metadata) from backend DynamoDB
   */
  async fetchSupplierMetadata(encodedCompanyId: string): Promise<{ contact_metadata: any, metadata: any, company_guid?: string }> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ contact_metadata: any, metadata: any, company_guid?: string }>(
          `${environment.fastApiBaseUrl}/ai/supplier/metadata`,
          { params: { company_id: encodedCompanyId } }
        )
      );
      return response;
    } catch (error) {
      console.error('Failed to fetch supplier metadata:', error);
      throw error;
    }
  }

  /**
   * Fetch supplier metadata using company GUID from QuoteNet API
   */
  async fetchSupplierMetadataByGuid(encodedCompanyGuid: string): Promise<{ contact_metadata: any, metadata: any, company_guid?: string }> {
    try {
      const response = await firstValueFrom(
        this.http.get<{
          status: boolean;
          data: {
            companyGUID: string;
            companyId: number;
            metaData: string;
            contactMetaData: string;
            createdOn: string;
          };
          message: string;
          errors: any[];
        }>(
          `${environment.dynamoApiBaseUrl}/Supplier/GetQASupplierDetailsByCompanyGuid?companyGuid=${encodedCompanyGuid}`
        )
      );

      // Transform the response to match the expected format
      if (response.status && response.data) {
        let parsedMetadata: any = null;
        let parsedContactMetadata: any = null;

        // Parse metaData JSON string
        try {
          parsedMetadata = response.data.metaData ? JSON.parse(response.data.metaData) : null;
        } catch (error) {
          console.warn('Failed to parse metaData JSON:', error);
          parsedMetadata = null;
        }

        // Parse contactMetaData JSON string
        try {
          parsedContactMetadata = response.data.contactMetaData ? JSON.parse(response.data.contactMetaData) : null;
        } catch (error) {
          console.warn('Failed to parse contactMetaData JSON:', error);
          parsedContactMetadata = null;
        }

        return {
          contact_metadata: parsedContactMetadata,
          metadata: parsedMetadata,
          company_guid: response.data.companyGUID
        };
      } else {
        throw new Error(response.message || 'Failed to fetch supplier metadata');
      }
    } catch (error) {
      console.error('Failed to fetch supplier metadata by GUID:', error);
      throw error;
    }
  }

  /**
   * Get the stored company GUID from localStorage
   */
  getStoredCompanyGuid(): string | null {
    // Get GUID from PreviewV2Service instead of localStorage to ensure consistency
    const guid = this.previewService.getCompanyGuid();
    return guid || null;
  }
  clearStoredCompanyGuid(): void {
  }

  async generateAboutUsContent(
    supplierData: SupplierData, 
    existingContent: Record<string, string>
  ): Promise<TagReplacement[]> {
    try {
      // Convert supplier data to business data format expected by API
      const businessData = this.convertSupplierDataToBusinessData(supplierData);
      
      console.log('AIContentService: Generating about us content for:', businessData.company_name);
      console.log('AIContentService: Using existing content context with', Object.keys(existingContent).length, 'tags');

      // Make HTTP request to about us generation endpoint
      const response = await firstValueFrom(
        this.http.post<TagBasedGenerationResponse>(
          `${environment.fastApiBaseUrl}/ai/generate-about-content`,
          {
            business_data: [businessData],
            existing_content: existingContent,
            content_preferences: this.getDefaultContentPreferences()
          }
        )
      );

      // Validate response and return tag replacements
      if (response.status === 'success' && response.data) {
        let tagReplacements: TagReplacement[];
        
        if (Array.isArray(response.data)) {
          tagReplacements = response.data;
        } else {
          // Convert dict structure to TagReplacement[]
          tagReplacements = this.convertDictToTagReplacements(response.data as DictContentStructure);
        }
        
        return tagReplacements;
      } else {
        console.warn('AIContentService: About us generation returned no data:', response);
        return []; // Return empty array for safe fallback
      }
      
    } catch (error) {
      console.error('AIContentService: About us content generation failed:', error);
      
      // Log detailed error information for debugging
      if (error instanceof Error) {
        console.error('AIContentService: Error details:', error.message);
      }
      
      // Return empty array for graceful degradation - existing content will be preserved
      return [];
    }
  }

  /**
   * Convert SupplierData to BusinessData format expected by AI API
   */
  convertSupplierDataToBusinessData(supplierData: SupplierData): BusinessData {
    // Parse capabilities string into array
    const capabilitiesArray = supplierData.capabilities 
      ? supplierData.capabilities.split(',').map(cap => cap.trim()).filter(cap => cap.length > 0)
      : [];

    // Extract additional capabilities if present in equipment field
    let equipment = supplierData.equipment || '';
    let otherCapabilities = '';

    return {
      contact_email: supplierData.contactEmail || undefined,
      company_name: supplierData.companyName,
      company_desc: supplierData.aboutContent || undefined,
      current_website_url: supplierData.websiteURL || undefined,
      capabilities: capabilitiesArray.length > 0 ? capabilitiesArray : undefined,
      other_capabilities: supplierData.other_capabilities || undefined,
      equipment: equipment || undefined,
      industry: 'Manufacturing', // Default for manufacturing focus
      target_audience: 'B2B Manufacturing Clients'
    };
  }

  /**
   * Get default content preferences for manufacturing domain
   */
  getDefaultContentPreferences(): ContentPreferences {
    return {
      tone: 'professional',
      style: 'manufacturing',
      industry: 'US Manufacturing',
      key_messages: ['precision', 'quality', 'reliability', 'experience']
    };
  }

  /**
   * Extract HTML tags from template content
   * This is a client-side helper - the actual extraction happens on the server
   */
  extractTagsFromHtml(htmlContent: string): Array<{tag_name: string, context: string}> {
    const tagPattern = /tag="([^"]+)"/g;
    const extractedTags: Array<{tag_name: string, context: string}> = [];
    const seenTags = new Set<string>();
    
    let match;
    while ((match = tagPattern.exec(htmlContent)) !== null) {
      const tagName = match[1];
      if (!seenTags.has(tagName)) {
        extractedTags.push({
          tag_name: tagName,
          context: this.inferTagContext(htmlContent, tagName)
        });
        seenTags.add(tagName);
      }
    }
    
    return extractedTags;
  }

  /**
   * Infer context for a tag based on its surroundings
   */
  private inferTagContext(htmlContent: string, tagName: string): string {
    const tagPattern = new RegExp(`<[^>]*tag="${tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"[^>]*>`, 'i');
    const match = htmlContent.match(tagPattern);
    
    if (match) {
      const element = match[0];
      
      if (/<h[1-6]/i.test(element)) {
        return "Headline or title content";
      } else if (/class="highlight"/i.test(element) || /<span/i.test(element)) {
        return "Highlighted text or tagline";
      } else if (/<p/i.test(element)) {
        return "Paragraph or description content";
      } else if (/hero|banner/i.test(element)) {
        return "Hero section content";
      } else if (/about/i.test(element)) {
        return "About section content";
      } else if (/capab|service/i.test(element)) {
        return "Capabilities or services content";
      } else if (/footer|contact/i.test(element)) {
        return "Footer or contact content";
      }
    }
    
    // Fallback to tag name analysis
    const tagLower = tagName.toLowerCase();
    if (/header|title|headline/i.test(tagLower)) {
      return "Headline or title content";
    } else if (/subtitle|tagline/i.test(tagLower)) {
      return "Subtitle or tagline content";
    } else if (/desc|description/i.test(tagLower)) {
      return "Description content";
    } else if (/about/i.test(tagLower)) {
      return "About section content";
    } else if (/capab|service/i.test(tagLower)) {
      return "Capabilities or services content";
    } else if (/footer|contact/i.test(tagLower)) {
      return "Footer or contact content";
    }
    
    return `Content for ${tagName}`;
  }
} 